import React from "react";
import { Box } from "@mui/joy";
import { PlanningCalendarGrid } from "components/common/PlanningCalendar";
import type { DayCellAction } from "components/common/PlanningCalendar";
import type { LeaveRequest, LeaveType } from "api/leave/Leave.typed";
import type { ExcursionBooking } from "api/excursions/Excursion.typed";
import type { LeaveMotifVisibilityOpts } from "utils/planningLeaveHelpers";
import { PlanningTooltipExclusiveProvider } from "./PlanningTooltip";
import DayBookingsGroup from "./DayBookingsGroup";
import DayLeaveChip from "./DayLeaveChip";

export type PlanningCalendarGridProps = {
  viewMode: "week" | "month";
  days: Date[];
  monthGridOffset: number;
  getRequestsForDay: (date: Date) => LeaveRequest[];
  typesMap: Record<number, { Color: string; Name: string }>;
  getBookingsForDay?: (date: Date) => ExcursionBooking[];
  getUserName?: (id: number) => string | undefined;
  currentUserId?: number;
  canValidateAnyBooking?: boolean;
  onBookingStatusChange?: (bookingId: number, status: "validated" | "rejected") => Promise<void>;
  onDayCellClickLeaveRequest?: (day: Date) => void;
  dayCellActions?: DayCellAction[];
  showMeViewCompanyCartouche?: boolean;
  viewerTenantCompanyId?: number;
  companyTimeZone?: string | null;
  leaveTypes?: LeaveType[];
  viewerHasPlanningMeView?: boolean;
  autoScrollToToday?: boolean;
};

const PlanningLeaveBookingsGrid = React.memo(function PlanningLeaveBookingsGrid({
  viewMode,
  days,
  monthGridOffset,
  getRequestsForDay,
  typesMap,
  getBookingsForDay,
  getUserName,
  currentUserId,
  canValidateAnyBooking = false,
  onBookingStatusChange,
  onDayCellClickLeaveRequest,
  dayCellActions,
  viewerTenantCompanyId,
  companyTimeZone,
  leaveTypes,
  viewerHasPlanningMeView = false,
  autoScrollToToday = false,
}: PlanningCalendarGridProps) {
  const leaveMotifVisibilityOpts = React.useMemo<LeaveMotifVisibilityOpts>(
    () => ({ viewerUserId: currentUserId ?? null, viewerHasPlanningMeView }),
    [currentUserId, viewerHasPlanningMeView],
  );

  const renderDayContent = (day: Date) => {
    const dayRequests = getRequestsForDay(day);
    const dayBookings = getBookingsForDay?.(day) ?? [];
    if (dayRequests.length === 0 && dayBookings.length === 0) return null;

    const dayKey = `${day.getFullYear()}${day.getMonth()}${day.getDate()}`;

    return (
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, minWidth: 0 }}>
        <DayBookingsGroup
          dayBookings={dayBookings}
          dayKey={dayKey}
          currentUserId={currentUserId}
          viewerTenantCompanyId={viewerTenantCompanyId}
          getUserName={getUserName}
          canValidateAnyBooking={canValidateAnyBooking}
          onBookingStatusChange={onBookingStatusChange}
        />

        {dayRequests.map((request) => (
          <DayLeaveChip
            key={`r-${request.id}`}
            request={request}
            dayKey={dayKey}
            typesMap={typesMap}
            getUserName={getUserName}
            viewerTenantCompanyId={viewerTenantCompanyId}
            companyTimeZone={companyTimeZone}
            leaveTypes={leaveTypes}
            leaveMotifVisibilityOpts={leaveMotifVisibilityOpts}
          />
        ))}
      </Box>
    );
  };

  return (
    <PlanningTooltipExclusiveProvider>
      <PlanningCalendarGrid
        viewMode={viewMode}
        days={days}
        monthGridOffset={monthGridOffset}
        renderDayContent={renderDayContent}
        dayHeaderFormat="dayMonth"
        fillHeight
        dayCellActions={dayCellActions}
        autoScrollToToday={autoScrollToToday}
      />
    </PlanningTooltipExclusiveProvider>
  );
});

export default PlanningLeaveBookingsGrid;
